import { FeedbackPerspective } from '@/core/models/feedback-perspective'
import type { Scale } from '@/core/models/scale'
import type { ScaleItemCount } from '@/core/models/surveys-analytics'
import { SELF_PERSPECTIVE_KEY } from '@/core/models/perspective'
import { computed, onMounted, ref, toValue, type MaybeRefOrGetter } from 'vue'

const MAX_BAR_HEIGHT = 96

/** Even series use the container tone; odd series use the solid primary tone. */
export const histogramBarColor = (index: number) =>
  index % 2 === 0 ? 'bg-primary-container' : 'bg-primary'

export type HistogramPerspectiveDistribution = {
  readonly key: string
  readonly distribution: ReadonlyArray<ScaleItemCount>
}

export const useHistogramBars = (
  scale: MaybeRefOrGetter<Scale>,
  distributions: MaybeRefOrGetter<ReadonlyArray<HistogramPerspectiveDistribution>>,
  showSelfFeedback: MaybeRefOrGetter<boolean> = false,
) => {
  const animated = ref(false)

  onMounted(() => {
    setTimeout(() => {
      animated.value = true
    }, 10)
  })

  const visibleDistributions = computed(() => {
    const includeSelf = toValue(showSelfFeedback)
    return toValue(distributions).filter(
      (entry) =>
        (includeSelf || entry.key !== SELF_PERSPECTIVE_KEY) &&
        entry.distribution.some((item) => item.count > 0),
    )
  })

  const hasNonSelfFeedback = computed(
    () => visibleDistributions.value.some((entry) => entry.key !== SELF_PERSPECTIVE_KEY),
  )

  const maxCount = computed(() => {
    const counts = visibleDistributions.value.flatMap((entry) =>
      entry.distribution.map((item) => item.count),
    )
    return Math.max(...counts, 1)
  })

  const bars = computed(() => {
    const scaleItems = toValue(scale).items

    return scaleItems.map((item) => {
      const values = visibleDistributions.value.map((entry) => ({
        count: entry.distribution.find((d) => d.value == item.value)?.count ?? 0,
        perspective: entry.key,
      }))

      return {
        scaleItem: item,
        values,
      }
    })
  })

  const barHeight = (count: number) => {
    if (count <= 0 || !animated.value) {
      return '4px'
    }
    return `${(count / maxCount.value) * MAX_BAR_HEIGHT}px`
  }

  const hasOnlyOnePerspective = computed(() => visibleDistributions.value.length === 1)

  /** @deprecated Use hasNonSelfFeedback — kept for gradual call-site updates */
  const hasPeerFeedback = hasNonSelfFeedback
  const hasExternalFeedback = computed(() => false)

  return {
    bars,
    barHeight,
    hasPeerFeedback,
    hasExternalFeedback,
    hasNonSelfFeedback,
    hasOnlyOnePerspective,
    FeedbackPerspective,
  }
}
